import { COMPLIANCE_SCOPE_LABELS, type ComplianceDocumentType } from "@station/contracts";
import {
  Alert,
  Button,
  DataTable,
  type DataTableColumn,
  EmptyState,
  StatusBadge,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { BadgeCheck, Plus, ShieldCheck } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { reminderDaysText } from "../../../features/compliance/compliance-values.js";
import { useComplianceTypes, useRefreshCompliance } from "../../../features/compliance/queries.js";
import { AdoptDialog, TypeDialog } from "../../../features/compliance/type-dialogs.js";
import { useMaintenancePermissions } from "../../../features/maintenance/queries.js";
import { MaintenancePage } from "../../../features/maintenance/shared.js";
import { api } from "../../../lib/api.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatCount, formatDateTime } from "../../../lib/format.js";

export const Route = createFileRoute("/_app/uyum/turler")({ component: TypesRoute });

/**
 * Uyum belge türleri (Prompt 23 web, `GET /compliance/types`): kapsam, önerilen geçerlilik, hatırlatma günleri, doğrulama
 * durumu ve etkin belge sayısı. Yasal süreler KODDA DEĞİLDİR: öneriden gelen tür kiracı tarafından doğrulanır ("doğrulayın"
 * rozeti), kiracı kendi türünü de tanımlar.
 */
function TypesRoute(): ReactElement {
  const { t } = useTranslation();
  const permissions = useMaintenancePermissions();
  const [dialog, setDialog] = useState<"adopt" | "new" | ComplianceDocumentType | null>(null);
  return (
    <MaintenancePage
      title={t("menu.complianceTypes")}
      description={t("compliance.types.description")}
      requirement="compliance"
      actions={
        permissions.canManageCompliance ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setDialog("adopt")}>
              <BadgeCheck aria-hidden="true" />
              {t("compliance.types.adopt")}
            </Button>
            <Button onClick={() => setDialog("new")}>
              <Plus aria-hidden="true" />
              {t("compliance.types.new")}
            </Button>
          </div>
        ) : null
      }
    >
      <TypeList onEdit={permissions.canManageCompliance ? (type) => setDialog(type) : null} />
      {dialog === "adopt" ? <AdoptDialog open onOpenChange={() => setDialog(null)} /> : null}
      {dialog === "new" ? <TypeDialog open onOpenChange={() => setDialog(null)} type={null} /> : null}
      {dialog && typeof dialog === "object" ? (
        <TypeDialog open onOpenChange={() => setDialog(null)} type={dialog} />
      ) : null}
    </MaintenancePage>
  );
}

function TypeList({ onEdit }: { onEdit: ((type: ComplianceDocumentType) => void) | null }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshCompliance();
  const types = useComplianceTypes();
  const verify = useMutation({
    mutationFn: async (type: ComplianceDocumentType) =>
      (
        await api.POST("/compliance/types/{id}/verify", {
          params: { path: { id: type.id } },
          body: { version: type.version },
        })
      ).data!,
    onSuccess: async (type) => {
      await refresh();
      toast({ tone: "success", title: t("compliance.types.verified", { name: type.name }) });
    },
    onError: (caught) => toast({ tone: "danger", title: errorMessage(caught) }),
  });
  const columns = useMemo<DataTableColumn<ComplianceDocumentType>[]>(
    () => [
      {
        id: "name",
        header: t("compliance.fields.typeName"),
        hideable: false,
        cell: (row) => (
          <span className="grid min-w-56 gap-0.5">
            <span className="font-medium">{row.name}</span>
            <span className="text-xs text-muted-foreground">{row.code}</span>
          </span>
        ),
      },
      {
        id: "scope",
        header: t("compliance.fields.appliesTo"),
        cell: (row) => COMPLIANCE_SCOPE_LABELS[row.appliesTo],
      },
      {
        id: "validity",
        header: t("compliance.fields.validityMonths"),
        cell: (row) =>
          row.validityMonths
            ? t("compliance.types.validityMonths", { count: row.validityMonths })
            : t("compliance.types.noValidity"),
      },
      {
        id: "reminders",
        header: t("compliance.fields.reminderDays"),
        cell: (row) => (row.reminderDays.length > 0 ? reminderDaysText(row.reminderDays) : "—"),
      },
      {
        id: "verification",
        header: t("compliance.fields.verification"),
        cell: (row) =>
          row.verified ? (
            <span className="grid justify-items-start gap-0.5">
              <StatusBadge tone="success">{t("compliance.types.verifiedBadge")}</StatusBadge>
              {row.verifiedAt ? (
                <span className="text-xs text-muted-foreground">
                  {row.verifiedByName} · {formatDateTime(row.verifiedAt)}
                </span>
              ) : null}
            </span>
          ) : (
            <span className="grid justify-items-start gap-1">
              <StatusBadge tone="warning">{t("compliance.types.unverifiedBadge")}</StatusBadge>
              {onEdit ? (
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto px-0"
                  loading={verify.isPending && verify.variables?.id === row.id}
                  onClick={() => {
                    if (window.confirm(t("compliance.types.verifyConfirm", { name: row.name })))
                      verify.mutate(row);
                  }}
                >
                  {t("compliance.types.verify")}
                </Button>
              ) : null}
            </span>
          ),
      },
      {
        id: "documents",
        header: t("compliance.fields.activeDocuments"),
        align: "right",
        cell: (row) => <span className="tabular">{formatCount(row.activeDocumentCount)}</span>,
      },
      {
        id: "active",
        header: t("maintenance.fields.status"),
        cell: (row) =>
          row.isActive ? (
            <StatusBadge tone="success">{t("common.active")}</StatusBadge>
          ) : (
            <StatusBadge>{t("common.inactive")}</StatusBadge>
          ),
      },
      ...(onEdit
        ? [
            {
              id: "actions",
              header: t("common.actions"),
              hideable: false,
              cell: (row: ComplianceDocumentType) => (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEdit(row)}
                  aria-label={t("compliance.types.editNamed", { name: row.name })}
                >
                  {t("common.edit")}
                </Button>
              ),
            },
          ]
        : []),
    ],
    [t, onEdit, verify],
  );
  return (
    <DataTable
      caption={t("menu.complianceTypes")}
      columns={columns}
      rows={types.data?.items ?? []}
      rowKey={(row) => row.id}
      loading={types.isPending}
      error={types.error ? <Alert tone="danger">{errorMessage(types.error)}</Alert> : undefined}
      emptyState={
        <EmptyState
          headingLevel={2}
          icon={<ShieldCheck />}
          title={t("compliance.types.emptyTitle")}
          description={t("compliance.types.emptyDescription")}
        />
      }
    />
  );
}
